import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const errMsg = (e) => apiError(e?.response?.data?.detail) || e?.message;

export const SPEC_FIELDS = [
  ["thread_type", "Thread Type"],
  ["thread_size", "Thread Size"],
  ["cable_type", "Cable Type"],
  ["armour_type", "Armour Type"],
  ["cable_diameter", "Cable Diameter Range"],
  ["material", "Material"],
  ["ex_protection", "Ex Protection"],
  ["gas_group", "Gas Group"],
  ["epl", "Equipment Protection Level"],
  ["ip_rating", "IP Rating"],
  ["temperature", "Temperature Range"],
  ["certificate_reference", "IECEx Certificate"],
  ["atex_certificate", "ATEX Certificate"],
];

export const SPEC_INPUTS = [
  ["thread_type", "Thread type", "text"],
  ["thread_size", "Thread size", "text"],
  ["cable_type", "Cable type", "text"],
  ["armour_type", "Armour type", "text"],
  ["cable_diameter_min", "Cable dia. min (mm)", "number"],
  ["cable_diameter_max", "Cable dia. max (mm)", "number"],
  ["material", "Material", "text"],
  ["ex_protection", "Ex protection", "text"],
  ["gas_group", "Gas group", "text"],
  ["epl", "EPL", "text"],
  ["ip_rating", "IP rating", "text"],
  ["temp_min", "Temp min (°C)", "number"],
  ["temp_max", "Temp max (°C)", "number"],
  ["certificate_reference", "IECEx certificate", "text"],
  ["atex_certificate", "ATEX certificate", "text"],
];

export const MANDATORY_DEFAULT = ["thread_size", "armour_type", "cable_diameter", "ex_protection",
  "gas_group", "ip_rating"];

export function specValue(spec, key) {
  if (!spec) return "Not stated";
  if (key === "cable_diameter")
    return spec.cable_diameter_min != null
      ? `${spec.cable_diameter_min} to ${spec.cable_diameter_max} mm`
      : "Not stated";
  if (key === "temperature")
    return spec.temp_min != null ? `${spec.temp_min} to ${spec.temp_max} °C` : "Not stated";
  return spec[key] || "Not stated";
}

export const money = (v, currency = "AED") =>
  v == null ? "—" : `${currency} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const INCOTERMS = ["", "EXW", "FCA", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
export const UNITS = ["PCS", "SETS", "METERS", "BOXES", "LOT"];
export const EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"];
export const UAE = "United Arab Emirates";
export const licenceLabel = (country) =>
  country === UAE ? "Trade licence number *" : "Business / company registration number *";
export const COUNTRIES = ["United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Oman", "Bahrain",
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize",
  "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic",
  "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Brazzaville)", "Congo (Kinshasa)", "Costa Rica",
  "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus", "Czechia", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hong Kong",
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
  "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macau", "Madagascar", "Malawi",
  "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea",
  "North Macedonia", "Norway", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
  "Peru", "Philippines", "Poland", "Portugal", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
  "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo",
  "Tonga", "Trinidad and Tobago", "Tunisia", "Türkiye", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
  "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"];
export const SUPPLIER_TYPES = [
  ["manufacturer", "Manufacturer"],
  ["distributor", "Authorized Distributor"],
  ["stockist", "Stockist"],
  ["trading", "Trading Company"],
];

export async function uploadFile(file, kind = "other") {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/uploads?kind=${encodeURIComponent(kind)}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
